<template>
  <fs-popper-event-wrapper
    ref="fsPopperRef"
    :tag="tag"
    :trigger="trigger"
    :offset="offset"
    :is-delayed="isDelayed"
    :can-open="canOpen"
    animation="slide-fade-fast"
    :default-class="`
      popper
      ${color === 'white' && 'popper--white'}
      ${placement === 'bottom' && 'popper--bottom'}
      ${placement === 'right' && 'popper--right'}
    `"
    :placement="placement"
    :class="['fs-tooltip', { 'fs-tooltip--clickable': trigger === 'click' }]"
    @pointerenter="onPointerEnter"
  >
    <slot />

    <template #popper>
      <span class="popper__text">
        <template v-if="text">
          {{ text }}
        </template>
        <template v-else-if="$slots.tooltipContent">
          <slot name="tooltipContent" />
        </template>
        <template v-else>
          <slot />
        </template>
      </span>

      <span
        :class="{
          'popper__caret-icon': true,
          'popper__caret-icon--bottom': placement === 'bottom',
          'popper__caret-icon--right': placement === 'right',
        }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="512" height="171" viewBox="0 0 512 171">
          <path d="M256.016,170.646L-0.011.181H512.042Z" />
        </svg>
      </span>
    </template>
  </fs-popper-event-wrapper>
</template>

<script lang="ts" setup>
// Components
import FsPopperEventWrapper from '@components/poppers/fs-popper-event-wrapper.vue';
import { computed, ref, watch } from 'vue';
import type { Options } from '@popperjs/core/lib/modifiers/offset';

const props = withDefaults(
  defineProps<{
    text?: string;
    color?: 'white' | 'gray';
    // Modifier quick-option - see Popper docs here https://popper.js.org/docs/v2/modifiers/offset/
    offset?: Options['offset'];
    placement?: 'top' | 'bottom' | 'right';
    isDelayed?: boolean;
    // Show tooltip even if hovered content is not ellipsed
    showEvenIfNotEllipsed?: boolean;
    /**
     * Conditionally set when tooltip should show or not, overrides 'showEvenIfNotEllipsed' and
     * default behaviour.
     *
     * Pass a function for a condition that changes often. It is asked when the pointer arrives
     * rather than while rendering, so whatever it reads never re-renders the tooltip — which is
     * what a table needs to keep its cells' tooltips down through a drag without re-rendering
     * every one of them.
     */
    show?: boolean | (() => boolean);
    tag?: string;
    // How the tooltip is revealed — defaults to hover. Pass 'click' for an
    // on-demand tooltip that only opens when the user clicks the content.
    triggerMode?: 'hover' | 'click';
  }>(),
  {
    color: 'gray',
    placement: 'top',
    isDelayed: false,
    showEvenIfNotEllipsed: false,
    show: true,
    tag: 'span',
  }
);

const slots = defineSlots<{
  default: () => any;
  tooltipContent?: () => any;
}>();

const fsPopperRef = ref<InstanceType<typeof FsPopperEventWrapper> | undefined>();
const isTooltipNeeded = ref<boolean>(false);
const isMeasured = ref<boolean>(false);

/** Whether the content has anything to say, before asking whether it is allowed to say it. */
const trigger = computed(function () {
  // A function `show` is deliberately not called here — asking it would put whatever it reads
  // into this render, which is the cost it exists to avoid. It is asked in `canOpen` instead.
  const mayShow = typeof props.show === 'function' || props.show;

  if (mayShow && (props.showEvenIfNotEllipsed || slots.tooltipContent || isTooltipNeeded.value)) {
    return props.triggerMode ?? 'hover';
  }

  return 'none';
});

/**
 * The last word on whether the tooltip opens, asked by the wrapper as it opens rather than by
 * anything that renders. Stable, so passing it never patches the wrapper.
 */
const canOpen = () => (typeof props.show === 'function' ? props.show() : props.show !== false);

/**
 * Whether the content is ellipsed - and so whether there is anything to show - is measured on
 * the first hover rather than on mount.
 *
 * Measuring walks the content and reads scrollWidth/offsetWidth off it, which forces layout.
 * A table renders these by the dozen, each measuring between its neighbours' mounts, so every
 * read finds the layout freshly invalidated and pays for it again. Nothing before a hover can
 * show a tooltip, so nothing before a hover needs the answer.
 */
function onPointerEnter(event: PointerEvent) {
  if (isMeasured.value) return;
  isMeasured.value = true;

  const wasSilent = trigger.value === 'none';
  setIsTooltipNeeded();

  if (trigger.value === 'none') {
    // Measured while it was already opening, after a change of text: there is nothing to show.
    fsPopperRef.value?.close(event);
    return;
  }

  // The hover that measured is the hover that should open it: the handler the wrapper binds
  // for a live trigger is not on the element yet, and the pointer is already inside.
  if (wasSilent && trigger.value === 'hover') fsPopperRef.value?.open(event);
}

// New text is a new answer, taken on the next hover like the first one was.
watch(
  () => props.text,
  () => (isMeasured.value = false)
);

function setIsTooltipNeeded() {
  const el: HTMLElement = fsPopperRef.value?.$el;
  if (!el) return;

  // Imported names carry stray spaces, and a node that matches nothing is taken for non-text below
  // and always given a tooltip — so trim both sides, not just the DOM's.
  const text = props.text?.trim();

  let nodeWithTooltipText = el;
  // Get the last node/container for the text/ellipsis
  if (el.children?.length > 0) {
    const childrenWithTooltipText = Array.from(el.querySelectorAll('*')).filter((element: Element) => {
      // `value` is a number on <li>, <progress> and <meter>, so it cannot be trimmed blindly.
      const value: unknown = (element as HTMLInputElement).value;

      return text === element.textContent?.trim() || (typeof value === 'string' && text === value.trim());
    });

    nodeWithTooltipText = childrenWithTooltipText.at(-1) as HTMLElement;
  }

  // Content is something other than text
  if (!nodeWithTooltipText) {
    isTooltipNeeded.value = true;

    return;
  }

  const { scrollWidth, offsetWidth, scrollHeight, offsetHeight } = nodeWithTooltipText;
  const { offsetWidth: parentOffsetWidth, offsetHeight: parentOffsetHeight } = nodeWithTooltipText.parentElement ?? {};

  // A pixel of slack on the width, as the height already carries four: these are integers rounded
  // off a fractional layout, and text 41.25px wide reports 41 as its offsetWidth and 42 as its
  // scrollWidth. Read strictly, that pixel of rounding is a tooltip on text that is fully visible
  // — and a pixel of genuine clipping is not worth one.
  isTooltipNeeded.value =
    scrollWidth > offsetWidth + 1 ||
    (parentOffsetWidth ? offsetWidth > parentOffsetWidth + 1 : false) ||
    // Below is for clamped text (e.g. multi-line text with ellipsis)
    scrollHeight > offsetHeight + 4 ||
    (parentOffsetHeight ? offsetHeight > parentOffsetHeight + 4 : false);
}
</script>

<style scoped lang="scss">
@import '@styles/text';
@import '@styles/variables.scss';

.fs-tooltip {
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;

  // Click-to-reveal tooltips show the help cursor across their whole content so
  // the user knows it's interactive. Descendants (labels, inputs, suffixes, …)
  // reset the cursor to their own default, so force them all to inherit the
  // help cursor — the entire text/value reads as clickable, not just a sliver.
  &--clickable {
    cursor: help;
    :deep(*) {
      cursor: inherit !important;
    }
  }
}

// ============================================================================
// Popper
.popper {
  @include body--small;
  // No floor: a bubble sized to its text reads as a label on the thing it points at, where a fixed
  // width leaves a word like "Sort" adrift in the middle of a box. Long text still wraps at the max.
  max-width: 32rem;
  width: max-content;
  word-break: break-word;
  padding: 0.5rem 1.1rem;
  border-radius: 1rem;
  position: relative;
  top: -0.6rem;
  display: flex;
  justify-content: center;
  align-content: center;
  align-items: center;
  line-height: 2rem;
  color: #fff;
  background: $c-ink;

  &--bottom {
    top: 0.6rem;
  }

  &--right {
    top: 0;
    left: 0.6rem;
  }

  &--white {
    color: $c-dark;
    background: #fff;
    box-shadow: 0 7px 13px -4px rgba($c-dark, 0.25);

    :deep(.popper__caret-icon svg) {
      fill: #fff;
    }
  }

  &__text {
    white-space: pre-line;
  }

  &__caret-icon {
    width: 2rem;
    height: 0.7rem;
    display: flex;
    justify-content: center;
    align-content: center;
    align-items: center;
    position: absolute;
    inset: auto auto -0.6rem calc(50% - 1rem);

    &--bottom {
      inset: -0.6rem auto auto auto;
      transform: rotate(180deg);
    }

    // Turned to point left, on the bubble's left edge
    &--right {
      inset: calc(50% - 0.35rem) auto auto -1.35rem;
      transform: rotate(90deg);
    }

    :deep(svg) {
      fill: $c-ink;
    }
  }
}

// Popper.js flips the tooltip to the opposite side when there's no room for
// the requested placement (e.g. anchors right under the fixed navbar). The
// offset/caret styling above follows the `placement` PROP, not the flipped
// reality — mirror it here based on the ACTUAL rendered placement so the
// caret always points at the anchor.
:global([data-popper-placement^='bottom'] > .popper:not(.popper--bottom)) {
  top: 0.6rem;
}
:global([data-popper-placement^='bottom'] > .popper:not(.popper--bottom) .popper__caret-icon) {
  inset: -0.6rem auto auto calc(50% - 1rem);
  transform: rotate(180deg);
}
:global([data-popper-placement^='top'] > .popper.popper--bottom) {
  top: -0.6rem;
}
:global([data-popper-placement^='top'] > .popper.popper--bottom .popper__caret-icon) {
  inset: auto auto -0.6rem calc(50% - 1rem);
  transform: rotate(0);
}
</style>
